import { ArrowForward, StarBorder } from '@mui/icons-material';
import { Box, Button, Chip, Container, IconButton, Typography } from '@mui/material';
import anime from 'animejs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Autoplay, EffectCoverflow, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import productsData from '../data/products.json';
import ArrowIcon from './ArrowIcon';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';

const ProductSlider3D = () => {
  const { t, i18n } = useTranslation();
  const titleRef = useRef(null);
  const swiperRef = useRef(null);
  const [slideDimensions, setSlideDimensions] = useState({
    width: '380px',
    height: '520px'
  });

  useEffect(() => {
    const updateSlideDimensions = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setSlideDimensions({ width: '280px', height: '420px' });
      } else if (width < 900) {
        setSlideDimensions({ width: '320px', height: '460px' });
      } else {
        setSlideDimensions({ width: '380px', height: '520px' });
      }
    };

    updateSlideDimensions();
    window.addEventListener('resize', updateSlideDimensions);
    return () => window.removeEventListener('resize', updateSlideDimensions);
  }, []);

  // Handle language changes to prevent Swiper effects
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      const swiper = swiperRef.current.swiper;
      
      // Temporarily disable transitions during language change
      swiper.allowTouchMove = false;
      swiper.allowSlideNext = false;
      swiper.allowSlidePrev = false;
      
      // Small delay to prevent visual glitches
      const timer = setTimeout(() => {
        swiper.allowTouchMove = true;
        swiper.allowSlideNext = true;
        swiper.allowSlidePrev = true;
        
        // Update swiper to handle any layout changes
        swiper.update();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [i18n.language]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const text = t('products.title');
    if (!text) return;

    el.innerHTML = '';
    const chars = text.split('');
    chars.forEach((char) => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      span.style.display = 'inline-block';
      span.style.opacity = '0';
      span.style.transform = 'translateY(20px) rotateX(-90deg)';
      span.style.transformOrigin = '50% 100%';
      el.appendChild(span);
    });

    const spans = el.querySelectorAll('span');
    anime.remove(spans);
    anime({
      targets: spans,
      opacity: [0, 1],
      translateY: [20, 0],
      rotateX: [-90, 0],
      duration: 1200,
      delay: anime.stagger(40),
      easing: 'spring(1, 80, 10, 0)',
    });

    return () => {
      anime.remove(spans);
    };
  }, [i18n.language, t]);

  // Load product data from JSON file
  const products = productsData.products.map(product => ({
    ...product,
    name: t(`products.product${product.id}.name`) || product.name,
    description: t(`products.product${product.id}.desc`) || product.description,
  }));

  const getBadgeColor = (badge) => {
    const colors = {
      Popular: '#ff6b6b',
      New: '#4ecdc4',
      Featured: '#f39c12',
      Premium: '#9b59b6',
      Enterprise: '#3498db',
      Hot: '#e74c3c',
    };
    return colors[badge] || '#667eea';
  };

  return (
    <Box
      sx={{
        py: { xs: 4, sm: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'radial-gradient(ellipse at top, #1e293b 0%, #0f172a 100%)'
            : 'radial-gradient(ellipse at top, #f8fafc 0%, #e2e8f0 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%), ' +
            'radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
            mt: { xs: 8, sm: 6, md: 4, lg: 1 }, // Add responsive margin top for mobile hamburger menu
            opacity: 0,
            animation: 'fadeInUp 1s ease-out 0.2s forwards',
            '@keyframes fadeInUp': {
              '0%': { opacity: 0, transform: 'translateY(30px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {/* AI Solution Provider Header */}
          <Typography
            variant="overline"
            sx={{
              fontSize: { xs: '0.75rem', sm: '1rem', md: '1.5rem', lg: '2rem' },
              fontWeight: 1000,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              display: 'block',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {t('products.header') || 'AI Solution Provider'}
          </Typography>


          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
              color: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              maxWidth: { xs: '100%', sm: '600px', md: '700px' },
              mx: 'auto',
              lineHeight: 1.6,
              mt: { xs: 0.5, sm: 1 },
              px: { xs: 2, sm: 0 },
            }}
          >
            {t('products.subtitle') ||
              'Explore our cutting-edge products designed to transform your organization'}
          </Typography>
        </Box>

        {/* 3D Swiper Slider */}
        <Box
          sx={{
            position: 'relative',
            opacity: 0,
            animation: 'fadeIn 1.2s ease-out 0.4s forwards',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
          }}
        >
          <Swiper
            key={`swiper-${i18n.language}`}
            ref={swiperRef}
            effect={'coverflow'}
            loop={true}
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={'auto'}
            slideToClickedSlide={true}
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            navigation={false}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            modules={[EffectCoverflow, Pagination, Autoplay]}
            className="product-swiper"
            style={{
              padding: '20px 0 60px',
            }}
          >
            {products.map((product, index) => (
              <SwiperSlide
                key={product.id}
                style={{
                  width: slideDimensions.width,
                  height: slideDimensions.height,
                  cursor: 'pointer',
                }}
              // onClick={() => {
              //   if (swiperRef.current && swiperRef.current.swiper) {
              //     swiperRef.current.swiper.slideTo(index);
              //   }
              // }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(30, 41, 59, 0.9)'
                        : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.2)'
                        : '1px solid rgba(148, 163, 184, 0.3)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 35px 60px -12px rgba(0, 0, 0, 0.35)',
                      '& .product-image': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  {/* Badge */}
                  <Chip
                    label={product.badge}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      zIndex: 2,
                      backgroundColor: getBadgeColor(product.badge),
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    }}
                  />

                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '220px',
                      background: product.gradient,
                      opacity: 0.15,
                      zIndex: 0,
                    }}
                  />

                  {/* Product Image */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '220px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={product.image}
                      alt={product.name}
                      className="product-image"
                      sx={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        objectFit: 'contain',
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        filter: 'drop-shadow(0 10px 25px rgba(0, 0, 0, 0.15))',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback placeholder */}
                    <Box
                      sx={{
                        display: 'none',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: product.gradient,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          color: '#fff',
                          fontWeight: 700,
                          opacity: 0.7,
                        }}
                      >
                        {product.name.charAt(0)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Content */}
                  <Box
                    sx={{
                      p: 2.5,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        fontSize: '1.35rem',
                        color: (theme) =>
                          theme.palette.mode === 'dark' ? '#fff' : '#0f172a',
                        lineHeight: 1.3,
                      }}
                    >
                      {product.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1.5,
                        fontSize: '0.9rem',
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.6)',
                        lineHeight: 1.5,
                      }}
                    >
                      {product.description}
                    </Typography>

                    {/* Features */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {product.features.map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          icon={<StarBorder sx={{ fontSize: 14 }} />}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            background: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(99, 102, 241, 0.15)'
                                : 'rgba(99, 102, 241, 0.1)',
                            color: '#667eea',
                            border: '1px solid rgba(99, 102, 241, 0.2)',
                            '& .MuiChip-icon': {
                              color: '#667eea',
                            },
                          }}
                        />
                      ))}
                    </Box>

                    {/* Action Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForward />}
                      component={Link}
                      to={`/detailed-product?product=${product.id}`}
                      sx={{
                        py: 2,
                        my: 4,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        background: product.gradient,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {t('products.learnMore') || 'Learn More'}
                    </Button>
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Navigation Arrows */}
          <IconButton
            onClick={() => {
              if (swiperRef.current && swiperRef.current.swiper) {
                swiperRef.current.swiper.slidePrev();
              }
            }}
            sx={{
              position: 'absolute',
              left: '-50px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: '56px',
              height: '56px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              color: '#6ca12b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
              display: { xs: 'none', md: 'flex' },
            }}
          >
            <ArrowIcon sx={{ transform: 'rotate(180deg)', fill: '#000000' }} />
          </IconButton>

          <IconButton
            onClick={() => {
              if (swiperRef.current && swiperRef.current.swiper) {
                swiperRef.current.swiper.slideNext();
              }
            }}
            sx={{
              position: 'absolute',
              right: '-50px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: '56px',
              height: '56px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              color: '#6ca12b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
              display: { xs: 'none', md: 'flex' },
            }}
          >
            <ArrowIcon sx={{ fill: '#000000' }} />
          </IconButton>
        </Box>

        {/* Our Products Button - Below Slider */}
        <Box
          sx={{
            textAlign: 'center',
            mt: 3,
            opacity: 0,
            animation: 'fadeInUp 1s ease-out 0.8s forwards',
            '@keyframes fadeInUp': {
              '0%': { opacity: 0, transform: 'translateY(20px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Chip
            label={t('products.label') || 'Our Products'}
            component={Link}
            to="/all-projects"
            clickable
            sx={{
              px: { xs: 4, sm: 5, md: 6 },
              py: { xs: 2, sm: 2.5, md: 3 },
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
              fontWeight: 700,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minHeight: { xs: '48px', sm: '52px', md: '56px' },
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 32px rgba(102, 126, 234, 0.6)',
              },
            }}
          />
        </Box>

        {/* Custom Swiper Styles */}
        <style>
          {`
            .product-swiper .swiper-pagination {
              bottom: 20px;
            }
            
            .product-swiper .swiper-pagination-bullet {
              width: 12px;
              height: 12px;
              background: rgba(108, 161, 43, 0.5);
              opacity: 1;
              transition: all 0.3s ease;
            }
            
            .product-swiper .swiper-pagination-bullet-active {
              background: #6ca12b;
              width: 32px;
              border-radius: 6px;
            }
            
            /* Prevent visual glitches during language changes */
            .product-swiper {
              transition: none !important;
            }
            
            .product-swiper .swiper-slide {
              transition: transform 0.3s ease;
            }
            
            /* Ensure smooth transitions */
            .product-swiper .swiper-wrapper {
              transition: transform 0.3s ease;
            }
          `}
        </style>
      </Container>
    </Box>
  );
};

export default ProductSlider3D;

